import { FRAME_NAMES } from '@/common/define/apiTrace.define';
import { ROLE_PERMISSION_KEY } from '@/common/define/rolePermission.define';
import { useMessage } from '@/common/inject/useMessage';
import { useInternational } from '@/common/locale';
import { useTargetPermissionCheck } from '@/common/permission/permission.utils';
import { TargetStatusRequestTargetKind } from '@/api-client/target/model';
import { getSlideDetailStore, TOTAL_DETAIL } from '@/common/stores/slide-detail';
import type {
  SlideDetailAdapterParams,
  SlideDetailAdapterResult,
  SlideDetailAdapterResultKey,
} from '@/common/types/slideDetailAdapter.types';
import { createSlideDetailResult } from '@/common/utils/slideDetailAdapterResult';
import type { RolePermissionKeyType } from '@/config/views/permission/rolePermission/rolePermission.type';
import { getV8InfraHostsInfoByHostId } from '@/api-client/infra/queries/infra-host-v8-controller/infra-host-v8-controller';
import { getRequiredMap } from '@/common/utils/core/detailFieldMeta.utils';
import { ref } from 'vue';
import { z } from 'zod';

const hostDetailInputSchema = z
  .object({
    targetId: z.string().optional(),
    targetName: z.string().optional(),
    hostId: z.string().optional(),
    hostName: z.string().optional(),
    hostAlias: z.string().optional(),
    server: z.string().optional(),
    fromTime: z.string().optional(),
    toTime: z.string().optional(),
    addition: z.record(z.string(), z.unknown()).optional(),
    rawData: z.record(z.any()).optional(),
  })
  .transform(
    ({
      targetId,
      hostId,
      hostAlias,
      hostName,
      targetName,
      server,
      fromTime,
      toTime,
      addition,
      rawData,
    }) => ({
      hostId: targetId || hostId || rawData?.hostId || rawData?.targetId,
      hostName: targetName || hostAlias || hostName || rawData?.hostName || rawData?.targetName,
      server: server || rawData?.server,
      fromTime: fromTime || rawData?.fromTime,
      toTime: toTime || rawData?.toTime,
      addition,
    }),
  )
  .pipe(
    z.object({
      hostId: z.string().min(1),
      hostName: z.string().optional(),
      server: z.string().optional(),
      fromTime: z.string().optional(),
      toTime: z.string().optional(),
      addition: z.record(z.string(), z.unknown()).optional(),
    }),
  );

export type HostDetailInput = z.input<typeof hostDetailInputSchema>;

export const hostDetailRequiredMap = getRequiredMap(hostDetailInputSchema);

const hostProcessDetailInputSchema = z
  .object({
    targetId: z.string().optional(),
    hostId: z.string().optional(),
    pid: z.number().optional(),
    processName: z.string().optional(),
    fromTime: z.string().optional(),
    toTime: z.string().optional(),
    rawData: z.record(z.any()).optional(),
  })
  .transform(({ targetId, hostId, rawData, ...rest }) => ({
    ...rest,
    hostId: targetId || hostId || rawData?.hostId || rawData?.targetId,
    pid: rest.pid ?? (rawData?.pid != null ? Number(rawData.pid) : undefined),
    processName: rest.processName ?? rawData?.processName,
    fromTime: rest.fromTime ?? rawData?.fromTime,
    toTime: rest.toTime ?? rawData?.toTime,
  }))
  .pipe(
    z.object({
      hostId: z.string().min(1),
      pid: z.number().optional(),
      processName: z.string().optional(),
      fromTime: z.string().optional(),
      toTime: z.string().optional(),
    }),
  );

export type HostProcessDetailInput = z.input<typeof hostProcessDetailInputSchema>;

export const hostProcessDetailRequiredMap = getRequiredMap(hostProcessDetailInputSchema);

export const useHostDetailAdapter = (rolePermissionKey?: RolePermissionKeyType) => {
  const DEFAULT_ROLE_PERMISSION_KEY = ROLE_PERMISSION_KEY.MONITORING.MONITORING_INFRASTRUCTURE_HOST;

  const isSlideDetailOpened = ref(false);
  const { showErrorMsg } = useMessage();
  const { t } = useInternational();
  const { addTabPanel, addSplitTabPanel, isDetailSlidePermissionDenied } = getSlideDetailStore();
  const { checkIsValidTarget } = useTargetPermissionCheck();

  const fetchHostName = async (hostId: string): Promise<string> => {
    const { data } = await getV8InfraHostsInfoByHostId(hostId, {
      frameName: FRAME_NAMES.HOST_DETAIL.INFORMATION,
    });
    const hostData = data?.data?.[0];
    const meta = hostData?.meta;
    const name = meta?.hostName || '';
    const ip = hostData?.network?.ip || '';
    const alias = meta?.hostAlias || '';

    const parts = [name];
    if (ip) parts[0] = `${name}(${ip})`;
    if (alias) parts.push(`(${alias})`);
    return parts.join(' ').trim();
  };

  const resolveHostName = async (params: {
    hostId: string;
    hostName?: string;
  }): Promise<string> => {
    if (params.hostId) {
      try {
        return await fetchHostName(params.hostId);
      } catch {
        return '';
      }
    }

    return params.hostName ?? '';
  };

  const validateHost = async <T extends { hostId: string; fromTime?: string; toTime?: string }>(
    schema: z.ZodSchema<T>,
    type: (typeof TOTAL_DETAIL)[keyof typeof TOTAL_DETAIL],
    params: unknown,
    options: {
      showErrorMessage: boolean;
      checkPermission?: boolean;
      checkParams?: boolean;
      checkTarget?: boolean;
    },
  ): Promise<{ reason: SlideDetailAdapterResultKey; parsedParams?: T }> => {
    const { checkPermission = true, checkParams = true, checkTarget = true } = options;

    // 1. 권한 체크
    if (
      checkPermission &&
      isDetailSlidePermissionDenied({
        rolePermissionKey: rolePermissionKey ?? DEFAULT_ROLE_PERMISSION_KEY,
        type,
        showError: options.showErrorMessage,
      })
    ) {
      return { reason: 'permission_denied' };
    }

    // 2. 파라미터 검증
    // checkParams=false이면 에러 메시지 없이 silent하게 invalid_params 반환
    // (파싱 실패 시 hostId를 얻을 수 없어 타겟 체크도 불가)
    const parsed = schema.safeParse(params);
    if (!parsed.success) {
      if (checkParams && options.showErrorMessage) {
        const missingParams = parsed.error.issues.map((issue) => issue.path[0]).join(', ');
        showErrorMsg({
          msgStr: t('MESSAGE.LINK_MISSING_REQUIRED_PARAMS', { value: missingParams }),
        });
      }
      return { reason: 'invalid_params' };
    }

    // 3. 타겟 유효성 검증
    if (checkTarget) {
      const targetValidation = await checkIsValidTarget(
        { targetId: parsed.data.hostId, targetKind: TargetStatusRequestTargetKind.HOST },
        { showErrorMessage: options.showErrorMessage },
      );
      if (!targetValidation.valid) {
        return { reason: targetValidation.reason };
      }
    }

    return { reason: 'ok', parsedParams: parsed.data };
  };

  // ── Host Detail ─────────────────────────────────────────────────────────

  const validateHostDetail = async (
    params: HostDetailInput,
    options: {
      showErrorMessage: boolean;
      checkPermission?: boolean;
      checkParams?: boolean;
      checkTarget?: boolean;
    },
  ): Promise<{
    reason: SlideDetailAdapterResultKey;
    parsedParams?: z.infer<typeof hostDetailInputSchema> & { hostName: string };
  }> => {
    const result = await validateHost(hostDetailInputSchema, TOTAL_DETAIL.HOST, params, options);
    if (result.reason !== 'ok' || !result.parsedParams) return result;

    const { hostId, hostName } = result.parsedParams;
    const resolvedHostName = await resolveHostName({ hostId, hostName });
    return { reason: 'ok', parsedParams: { ...result.parsedParams, hostName: resolvedHostName } };
  };

  const openHostDetail = async ({
    params,
    options,
    selectedTab,
    state,
    currentState,
    init,
  }: SlideDetailAdapterParams<HostDetailInput>): Promise<SlideDetailAdapterResult> => {
    const {
      runCheckPermission = true,
      showErrorMessage = true,
      runTargetValidation = true,
      openMode = 'main',
      keepSplitTab = false,
    } = options ?? {};

    const { reason, parsedParams } = await validateHostDetail(params, {
      showErrorMessage,
      checkPermission: runCheckPermission,
      checkTarget: runTargetValidation,
    });
    if (reason !== 'ok' || !parsedParams) return createSlideDetailResult(reason);

    // 4. 탭 패널 열기
    const baseAddition = parsedParams.addition ?? { server: parsedParams.server };
    const tabPanelInfo = {
      type: TOTAL_DETAIL.HOST,
      host: parsedParams.hostId,
      name: parsedParams.hostName ?? '',
      addition: {
        ...baseAddition,
        fromTime: parsedParams.fromTime,
        toTime: parsedParams.toTime,
      },
      state: {
        ...state,
        selectedTab,
      },
      init,
      keepSplitTab: openMode === 'split' ? keepSplitTab : undefined,
    } as const;

    if (openMode === 'split') {
      addSplitTabPanel(tabPanelInfo);
      isSlideDetailOpened.value = true;
      return createSlideDetailResult('ok');
    }

    const result = addTabPanel(tabPanelInfo, currentState);
    isSlideDetailOpened.value = getSlideDetailStore().isOpened;
    return createSlideDetailResult(result);
  };

  // ── Host Process Detail ─────────────────────────────────────────────────

  const validateHostProcessDetail = async (
    params: HostProcessDetailInput,
    options: {
      showErrorMessage: boolean;
      checkPermission?: boolean;
      checkParams?: boolean;
      checkTarget?: boolean;
    },
  ): Promise<{
    reason: SlideDetailAdapterResultKey;
    parsedParams?: z.infer<typeof hostProcessDetailInputSchema>;
  }> => {
    return validateHost(hostProcessDetailInputSchema, TOTAL_DETAIL.PROCESS_DETAIL, params, options);
  };

  const openHostProcessDetail = async ({
    params,
    options,
    selectedTab,
    state,
    currentState,
    init,
  }: SlideDetailAdapterParams<HostProcessDetailInput>): Promise<SlideDetailAdapterResult> => {
    const {
      runCheckPermission = true,
      showErrorMessage = true,
      runTargetValidation = true,
      openMode = 'main',
      keepSplitTab = false,
    } = options ?? {};

    const { reason, parsedParams } = await validateHostProcessDetail(params, {
      showErrorMessage,
      checkPermission: runCheckPermission,
      checkTarget: runTargetValidation,
    });
    if (reason !== 'ok' || !parsedParams) return createSlideDetailResult(reason);

    // 4. 탭 패널 열기
    const tabPanelInfo = {
      type: TOTAL_DETAIL.PROCESS_DETAIL,
      name: parsedParams.processName || '',
      pid: parsedParams.pid || 0,
      host: parsedParams.hostId,
      addition: {
        fromTime: parsedParams.fromTime,
        toTime: parsedParams.toTime,
      },
      state: {
        ...state,
        selectedTab,
      },
      init,
      keepSplitTab: openMode === 'split' ? keepSplitTab : undefined,
    } as const;

    if (openMode === 'split') {
      addSplitTabPanel(tabPanelInfo);
      isSlideDetailOpened.value = true;
      return createSlideDetailResult('ok');
    }

    const result = addTabPanel(tabPanelInfo, currentState);
    isSlideDetailOpened.value = getSlideDetailStore().isOpened;
    return createSlideDetailResult(result);
  };

  return {
    isSlideDetailOpened,
    openHostDetail,
    openHostProcessDetail,
  };
};

export const useHostProcessDetailAdapter = (rolePermissionKey?: RolePermissionKeyType) => {
  const { isSlideDetailOpened, openHostProcessDetail } = useHostDetailAdapter(rolePermissionKey);
  return {
    isSlideDetailOpened,
    openHostProcessDetail,
  };
};
